import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Input from '../../components/input'
import FileUpload from '../../components/file-upload'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import { Actions as AuthActions } from '../../redux/auth/reducers'
import general from '../../utils/general'
import EventBus from '../../infrastructure/event-bus/event-bus'
import store from 'store'
import './StudentForm.scss'
import { getMissingStudentFields } from '../../utils/studentValidation'
import useI18n from '../../i18n/useI18n'

const defaultForm = {
  dateOfBirth: '',
  nationality: '',
  address: '',
  city: '',
  country: '',
  postalCode: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactRelation: '',
  educationLevel: '',
  gpa: '',
  languageEnglish: '',
  languageTurkish: '',
  passportFile: '',
  highschoolTranscript: '',
  highschoolDiploma: '',
  otherCv: '',
  otherMotivation: '',
  kvkkStatus: false,
  hobbies: ''
}

const StudentForm = ({ studentId = null, mode = 'admin', onSaved }) => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const isSelf = mode === 'self'
  const currentUserInfo = useSelector((state) => state.auth?.userInfo || {})

  const renderRequiredLabel = (text) => (
    <>
      {text} <span className="text-danger">*</span>
    </>
  )

  const [formData, setFormData] = useState(defaultForm)
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [currentStudentId, setCurrentStudentId] = useState(studentId)

  useEffect(() => {
    if (isSelf || studentId) {
      fetchStudent()
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, isSelf])

  const mapApiToForm = (data = {}) => ({
    dateOfBirth: data.dateOfBirth || '',
    nationality: data.nationality || '',
    address: data.address || '',
    city: data.city || '',
    country: data.country || '',
    postalCode: data.postalCode || '',
    emergencyContactName: data.emergencyContactName || '',
    emergencyContactPhone: data.emergencyContactPhone || '',
    emergencyContactRelation: data.emergencyContactRelation || '',
    educationLevel: data.educationLevel || '',
    gpa: data.gpa !== undefined && data.gpa !== null ? data.gpa : '',
    languageEnglish: data.languageProficiency?.english || '',
    languageTurkish: data.languageProficiency?.turkish || '',
    passportFile: data.passportFile || '',
    highschoolTranscript: data.highschoolTranscript || '',
    highschoolDiploma: data.highschoolDiploma || '',
    otherCv: data.otherFiles?.cv || '',
    otherMotivation: data.otherFiles?.motivationLetter || '',
    kvkkStatus: data.kvkkStatus ?? false,
    hobbies: data.additionalInfo?.hobbies || ''
  })

  const fetchStudent = () => {
    setLoading(true)
    setErrorMessage('')

    const url = isSelf ? 'students/me' : `students/${studentId}`

    dispatch(
      ApiCallActions.Get({
        url,
        showAlertOnError: true,
        showLoading: false,
        onSuccess: ({ data }) => {
          const student = data?.data || data
          setFormData(mapApiToForm(student))
          // Student ID'yi sakla (isSelf modunda API'den gelen ID'yi kullan)
          if (student?.id) {
            setCurrentStudentId(student.id)
          } else if (student?.user?.id) {
            setCurrentStudentId(student.user.id)
          }
          setLoading(false)
        },
        onError: ({ errorMessage }) => {
          setErrorMessage(errorMessage || t('STUDENT_FORM_ERROR_LOAD', 'Öğrenci bilgileri yüklenemedi'))
          setLoading(false)
        }
      })
    )
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const addStringToObject = (obj, key, value) => {
    if (value === undefined || value === null) return
    const trimmed = typeof value === 'string' ? value.trim() : value
    if (trimmed !== '') {
      obj[key] = trimmed
    }
  }

  const buildPayload = () => {
    const payload = {}
    const addString = (key, value) => {
      if (value === undefined || value === null) return
      const trimmed = typeof value === 'string' ? value.trim() : value
      if (trimmed !== '') {
        payload[key] = trimmed
      }
    }

    addString('dateOfBirth', formData.dateOfBirth)
    addString('nationality', formData.nationality)
    addString('address', formData.address)
    addString('city', formData.city)
    addString('country', formData.country)
    addString('postalCode', formData.postalCode)
    addString('emergencyContactName', formData.emergencyContactName)
    addString('emergencyContactPhone', formData.emergencyContactPhone)
    addString('emergencyContactRelation', formData.emergencyContactRelation)
    addString('educationLevel', formData.educationLevel)

    if (formData.gpa !== '' && !Number.isNaN(parseFloat(formData.gpa))) {
      payload.gpa = parseFloat(formData.gpa)
    }

    const languageProficiency = {}
    addStringToObject(languageProficiency, 'english', formData.languageEnglish)
    addStringToObject(languageProficiency, 'turkish', formData.languageTurkish)
    if (Object.keys(languageProficiency).length > 0) {
      payload.languageProficiency = languageProficiency
    }

    addString('passportFile', formData.passportFile)
    addString('highschoolTranscript', formData.highschoolTranscript)
    addString('highschoolDiploma', formData.highschoolDiploma)

    const otherFiles = {}
    addStringToObject(otherFiles, 'cv', formData.otherCv)
    addStringToObject(otherFiles, 'motivationLetter', formData.otherMotivation)
    if (Object.keys(otherFiles).length > 0) {
      payload.otherFiles = otherFiles
    }

    if (formData.kvkkStatus !== undefined) {
      payload.kvkkStatus = !!formData.kvkkStatus
    }

    const additionalInfo = {}
    addStringToObject(additionalInfo, 'hobbies', formData.hobbies)
    if (Object.keys(additionalInfo).length > 0) {
      payload.additionalInfo = additionalInfo
    }

    return payload
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrorMessage('')

    const missingFields = getMissingStudentFields(formData)
    if (missingFields.length > 0) {
      const translatedFields = missingFields.map(key => t(key))
      setErrorMessage(t('STUDENT_FORM_ERROR_MISSING_FIELDS', { fields: translatedFields.join(', '), defaultValue: `Lütfen şu alanları tamamlayın: ${translatedFields.join(', ')}` }))
      return
    }

    setIsSubmitting(true)
    const payload = buildPayload()
    const url = isSelf ? 'students/me' : `students/${studentId}`

    dispatch(
      ApiCallActions.Put({
        url,
        data: payload,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const savedData = data?.data || data
          setFormData(mapApiToForm(savedData))

          if (isSelf) {
            const updatedUserInfo = {
              ...currentUserInfo,
              student: savedData
            }
            store.set('studentProfile', savedData)
            store.set('userInfo', updatedUserInfo)
            dispatch(AuthActions.setAuthState({ userInfo: updatedUserInfo }))
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('studentProfileUpdated', { detail: savedData }))
            }
          }

          if (!isSelf) {
            EventBus.sc.onEntityUpdated({
              entityType: 'student',
              entityId: studentId || savedData?.id,
              entityData: savedData
            })
          }

          general.messageSuccess(t('STUDENT_FORM_SUCCESS_UPDATE', 'Öğrenci bilgileri güncellendi'))
          if (onSaved) {
            onSaved(savedData)
          }
        },
        onError: ({ errorMessage }) => {
          setErrorMessage(errorMessage || t('STUDENT_FORM_ERROR_UPDATE', 'Güncelleme sırasında bir hata oluştu'))
        },
        callback: () => setIsSubmitting(false)
      })
    )
  }

  if (loading) {
    return <p>{t('COMMON_LOADING', 'Yükleniyor...')}</p>
  }

  return (
    <>
      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <h5 className="m-b-5">{t('STUDENT_FORM_SECTION_IDENTITY', 'Kimlik & Lokasyon')}</h5>
          <small className="text-muted">{t('STUDENT_FORM_SUBSECTION_IDENTITY', 'Temel bilgiler ve adres')}</small>
        </div>
        <div className="row clearfix">
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{renderRequiredLabel(t('STUDENT_FORM_LABEL_DOB', 'Doğum Tarihi'))}</label>
              <Input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{renderRequiredLabel(t('STUDENT_FORM_LABEL_NATIONALITY', 'Uyruk'))}</label>
              <Input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                placeholder={t('STUDENT_FORM_PLACEHOLDER_NATIONALITY', 'Turkish')}
              />
            </div>
          </div>
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{renderRequiredLabel(t('STUDENT_FORM_LABEL_CITY', 'Şehir'))}</label>
              <Input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder={t('STUDENT_FORM_PLACEHOLDER_CITY', 'İstanbul')}
              />
            </div>
          </div>
        </div>

        <div className="row clearfix">
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{renderRequiredLabel(t('STUDENT_FORM_LABEL_COUNTRY', 'Ülke'))}</label>
              <Input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder={t('STUDENT_FORM_PLACEHOLDER_COUNTRY', 'Turkey')}
              />
            </div>
          </div>
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_ZIP', 'Posta Kodu')}</label>
              <Input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                placeholder={t('STUDENT_FORM_PLACEHOLDER_ZIP', '34000')}
              />
            </div>
          </div>
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_EDUCATION_LEVEL', 'Eğitim Seviyesi')}</label>
              <Input
                type="text"
                name="educationLevel"
                value={formData.educationLevel}
                onChange={handleChange}
                placeholder={t('STUDENT_FORM_PLACEHOLDER_EDUCATION_LEVEL', 'High School')}
              />
            </div>
          </div>
        </div>

        <div className="row clearfix">
          <div className="col-md-12">
            <div className="form-group">
              <label>{renderRequiredLabel(t('STUDENT_FORM_LABEL_ADDRESS', 'Adres'))}</label>
              <textarea
                className="form-control"
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="2"
                placeholder={t('STUDENT_FORM_PLACEHOLDER_ADDRESS', 'Adres bilgisi')}
              />
            </div>
          </div>
        </div>

        <hr className="student-section-divider" />
        <div className="mb-3">
          <h5 className="m-b-5">{t('STUDENT_FORM_SECTION_EDUCATION', 'Eğitim & Dil')}</h5>
          <small className="text-muted">{t('STUDENT_FORM_SUBSECTION_EDUCATION', 'Not ortalaması ve dil seviyeleri')}</small>
        </div>

        <div className="row clearfix">
          <div className="col-md-3 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_GPA', 'GPA')}</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="4"
                name="gpa"
                value={formData.gpa}
                onChange={handleChange}
                placeholder="3.50"
              />
            </div>
          </div>
          <div className="col-md-3 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_LANG_EN', 'İngilizce Seviyesi')}</label>
              <Input
                type="text"
                name="languageEnglish"
                value={formData.languageEnglish}
                onChange={handleChange}
                placeholder="B2"
              />
            </div>
          </div>
          <div className="col-md-3 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_LANG_TR', 'Türkçe Seviyesi')}</label>
              <Input
                type="text"
                name="languageTurkish"
                value={formData.languageTurkish}
                onChange={handleChange}
                placeholder="Native"
              />
            </div>
          </div>
          <div className="col-md-3 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_KVKK', 'KVKK Onayı')}</label>
              <div className="d-flex align-items-center">
                <input
                  type="checkbox"
                  name="kvkkStatus"
                  checked={!!formData.kvkkStatus}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                <span>{t('STUDENT_FORM_KVKK_APPROVED', 'Onaylı')}</span>
              </div>
            </div>
          </div>
        </div>
        <small className="text-muted mb-3">{t('STUDENT_FORM_NOTE_LANGUAGE', 'En az bir dil seviyesi girilmelidir.')}</small>

        <hr className="student-section-divider" />
        <div className="mb-3">
          <h5 className="m-b-5">{t('STUDENT_FORM_SECTION_EMERGENCY', 'Acil Durum')}</h5>
          <small className="text-muted">{t('STUDENT_FORM_SUBSECTION_EMERGENCY', 'Yakın kişi ve iletişim bilgileri')}</small>
        </div>

        <div className="row clearfix">
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_EMERGENCY_NAME', 'Acil Durum İletişim Adı')}</label>
              <Input
                type="text"
                name="emergencyContactName"
                value={formData.emergencyContactName}
                onChange={handleChange}
                placeholder="Ayşe Yılmaz"
              />
            </div>
          </div>
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_EMERGENCY_PHONE', 'Acil Durum Telefonu')}</label>
              <Input
                type="text"
                name="emergencyContactPhone"
                value={formData.emergencyContactPhone}
                onChange={handleChange}
                placeholder="+90555..."
              />
            </div>
          </div>
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_RELATION', 'Yakınlık')}</label>
              <Input
                type="text"
                name="emergencyContactRelation"
                value={formData.emergencyContactRelation}
                onChange={handleChange}
                placeholder={t('STUDENT_FORM_PLACEHOLDER_RELATION', 'Anne / Baba / Kardeş')}
              />
            </div>
          </div>
        </div>

        <hr className="student-section-divider" />
        <div className="mb-3">
          <h5 className="m-b-5">{t('STUDENT_FORM_SECTION_DOCUMENTS', 'Belgeler')}</h5>
          <small className="text-muted">{t('STUDENT_FORM_SUBSECTION_DOCUMENTS', 'Dosya yükleme ve görüntüleme')}</small>
        </div>

        <div className="row clearfix">
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <FileUpload
                uploadUrl={currentStudentId ? `files/students/${currentStudentId}/passport` : null}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                label={renderRequiredLabel(t('STUDENT_FORM_FILE_PASSPORT', 'Pasaport Dosyası'))}
                showPreview={false}
                fileType="document"
                currentFileUrl={formData.passportFile}
                disabled={!currentStudentId}
                onUploadSuccess={(uploadedData) => {
                  if (uploadedData && uploadedData.url) {
                    setFormData(prev => ({
                      ...prev,
                      passportFile: uploadedData.url
                    }))
                  }
                }}
                onRemove={() => {
                  setFormData(prev => ({
                    ...prev,
                    passportFile: ''
                  }))
                }}
              />
            </div>
          </div>
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <FileUpload
                uploadUrl={currentStudentId ? `files/students/${currentStudentId}/transcript` : null}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                label={renderRequiredLabel(t('STUDENT_FORM_FILE_TRANSCRIPT', 'Lise Transkript'))}
                showPreview={false}
                fileType="document"
                currentFileUrl={formData.highschoolTranscript}
                disabled={!currentStudentId}
                onUploadSuccess={(uploadedData) => {
                  if (uploadedData && uploadedData.url) {
                    setFormData(prev => ({
                      ...prev,
                      highschoolTranscript: uploadedData.url
                    }))
                  }
                }}
                onRemove={() => {
                  setFormData(prev => ({
                    ...prev,
                    highschoolTranscript: ''
                  }))
                }}
              />
            </div>
          </div>
          <div className="col-md-4 col-sm-12">
            <div className="form-group">
              <FileUpload
                uploadUrl={currentStudentId ? `files/students/${currentStudentId}/diploma` : null}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                label={renderRequiredLabel(t('STUDENT_FORM_FILE_DIPLOMA', 'Lise Diploma'))}
                showPreview={false}
                fileType="document"
                currentFileUrl={formData.highschoolDiploma}
                disabled={!currentStudentId}
                onUploadSuccess={(uploadedData) => {
                  if (uploadedData && uploadedData.url) {
                    setFormData(prev => ({
                      ...prev,
                      highschoolDiploma: uploadedData.url
                    }))
                  }
                }}
                onRemove={() => {
                  setFormData(prev => ({
                    ...prev,
                    highschoolDiploma: ''
                  }))
                }}
              />
            </div>
          </div>
        </div>

        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <div className="form-group">
              <FileUpload
                uploadUrl={currentStudentId ? `files/students/${currentStudentId}/cv` : null}
                accept=".pdf,.doc,.docx"
                maxSize={10}
                label={t('STUDENT_FORM_FILE_CV', 'CV')}
                showPreview={false}
                fileType="document"
                currentFileUrl={formData.otherCv}
                disabled={!currentStudentId}
                onUploadSuccess={(uploadedData) => {
                  if (uploadedData && uploadedData.url) {
                    setFormData(prev => ({
                      ...prev,
                      otherCv: uploadedData.url
                    }))
                  }
                }}
                onRemove={() => {
                  setFormData(prev => ({
                    ...prev,
                    otherCv: ''
                  }))
                }}
              />
            </div>
          </div>
          <div className="col-md-6 col-sm-12">
            <div className="form-group">
              <FileUpload
                uploadUrl={currentStudentId ? `files/students/${currentStudentId}/motivation-letter` : null}
                accept=".pdf,.doc,.docx"
                maxSize={10}
                label={t('STUDENT_FORM_FILE_MOTIVATION', 'Motivasyon Mektubu')}
                showPreview={false}
                fileType="document"
                currentFileUrl={formData.otherMotivation}
                disabled={!currentStudentId}
                onUploadSuccess={(uploadedData) => {
                  if (uploadedData && uploadedData.url) {
                    setFormData(prev => ({
                      ...prev,
                      otherMotivation: uploadedData.url
                    }))
                  }
                }}
                onRemove={() => {
                  setFormData(prev => ({
                    ...prev,
                    otherMotivation: ''
                  }))
                }}
              />
            </div>
          </div>
        </div>

        <hr className="student-section-divider" />
        <div className="mb-3">
          <h5 className="m-b-5">{t('STUDENT_FORM_SECTION_ADDITIONAL', 'Ek Bilgi')}</h5>
          <small className="text-muted">{t('STUDENT_FORM_SUBSECTION_ADDITIONAL', 'Hobiler ve notlar')}</small>
        </div>

        <div className="row clearfix">
          <div className="col-md-12">
            <div className="form-group">
              <label>{t('STUDENT_FORM_LABEL_HOBBIES', 'Ek Bilgi / Hobiler')}</label>
              <textarea
                className="form-control"
                name="hobbies"
                value={formData.hobbies}
                onChange={handleChange}
                rows="2"
                placeholder="Reading, Sports"
              />
            </div>
          </div>
        </div>

        <div className="row clearfix">
          <div className="col-md-12">
            <button
              type="submit"
              className="btn btn-primary btn-round"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('STUDENT_FORM_SAVING', 'Kaydediliyor...') : t('STUDENT_FORM_SAVE', 'Kaydet')}
            </button>
          </div>
        </div>
      </form>
    </>
  )
}

export default StudentForm


